/**
 * Cupom de entrega para impressão — fundo branco e texto preto para impressoras térmicas (TM-T20X).
 * Térmicas imprimem escuro sobre papel claro; fundo claro + texto preto = boa leitura.
 */
import axios from "axios";
import { smartPrint } from "./printConnector";
import { imprimirTickets } from "./cupomTicket";
import { carregarFormasDaLoja, nomeFormaAgora } from "../features/pedidos/lib/formasDaLoja";
import { fmtMoeda, fmtCnpj, fmtTelefone, formatCupomDate, formatCupomTime, buildLogoUrl, getEmpresaData, getImpressaoConfig, buildEmpresaPayload, buildFontScaleStyle, pizzaDisplayName, formatCupomAgendamento, medidaDoItem, qtdVezesPreco, getAllProductsCached, partesDoPagamento } from "./cupomFormatters";

const API = "/api";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/** O servidor vai imprimir o cupom de caixa deste pedido (nota fiscal saindo)? */
export async function servidorImprimeCaixa(pedidoId) {
  if (!pedidoId) return false;
  try {
    const { data } = await axios.get(`${API}/fiscal/cupom-caixa/${pedidoId}`, getAuthHeader());
    return Boolean(data?.servidor);
  } catch {
    return false; // na dúvida, a tela imprime: cupom a mais é melhor que venda sem cupom
  }
}

/**
 * Imprime o cupom do pedido em nova janela (fundo branco, texto preto — otimizado para térmica TM-T20X).
 * Busca configurações da empresa e, se o pedido não tiver itens, tenta buscar o pedido completo.
 * @param {Object} pedido - Pedido (items, cliente_nome, endereco_*, forma_pagamento, total, valor_entrega, etc.)
 * @param {Object} [opts] - { allProducts } — passa pra imprimirTickets evitar fetch redundante de /api/products;
 *   { automatico } — impressão automática da venda: com nota fiscal saindo, o cupom de caixa fica pro servidor
 * @throws {Error} Se o popup for bloqueado ou falha ao buscar dados
 */
export async function imprimirCupom(pedido, opts = {}) {
  let p = { ...pedido };

  // Buscar pedido e company em PARALELO (cache compartilhado)
  const needsPedido = (!p.items || !p.items.length) && p.id;
  const promises = [
    getEmpresaData(),
    carregarFormasDaLoja(), // nome das formas: o código "dinheiro_<loja>" não vai pro papel
    needsPedido ? axios.get(`${API}/pedidos/${p.id}`, getAuthHeader()).catch(() => null) : Promise.resolve(null),
    // Cadastro (quilo/litro): o item gravado não guarda a unidade de venda e
    // várias telas chamam sem `allProducts` — busca com cache (30/09/2026)
    opts.allProducts?.length ? Promise.resolve(opts.allProducts) : getAllProductsCached().catch(() => []),
  ];
  const [companyData, , pedidoRes, produtosCadastro] = await Promise.all(promises);
  if (pedidoRes?.data?.items?.length) p = pedidoRes.data;

  let company = { company_name: "Núcleo", slogan: "", address: "", cnpj: "", logo_url: null, phone: "", ...companyData };

  const codigoStr = String((p.codigo ?? "").replace(/^#+/, "")).padStart(5, "0");
  const nomeEmpresa = company.company_name || company.fantasy_name || "Núcleo";
  const isCancelado = (p.status || "").toLowerCase() === "cancelado";

  const logoUrl = buildLogoUrl(company);
  const impressaoConfig = await getImpressaoConfig();
  const _fontScaleCss = buildFontScaleStyle(impressaoConfig.font_scale);

  // Logo quadrada: 96px + 0,75% ≈ 97px — TM-T20X 80mm
  const logoSide = Math.round(96 * 1.0075);
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="Logo" style="width:${logoSide}px;height:${logoSide}px;object-fit:cover;display:block;margin:0 auto 8px;" onerror="this.style.display='none';var n=this.nextElementSibling;if(n)n.style.display='flex';" /><div style="display:none;width:${logoSide}px;height:${logoSide}px;background:#e0e0e0;margin:0 auto 8px;align-items:center;justify-content:center;font-size:11px;color:#333;">LOGO</div>`
    : `<div style="width:${logoSide}px;height:${logoSide}px;background:#e0e0e0;margin:0 auto 8px;display:flex;align-items:center;justify-content:center;font-size:11px;color:#333;">LOGO</div>`;

  let html;
  if (isCancelado) {
    const motivo = p.motivo_cancelamento || p.motivo || "Não informado";
    html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Cupom ${codigoStr}</title>
  <style>
    @page { size: 80mm auto; margin: 3mm; }
    body { margin: 0; padding: 8px; font-family: "Courier New", monospace; font-size: 13px; font-weight: bold; background: none; color: #000000; }
    .line { border-bottom: 1px dashed #000; margin: 6px 0; }
    .center { text-align: center; }
    .section { color: #000; font-weight: bold; margin: 4px 0; }
    @media print { body { max-width: 72.1mm; margin: 0 auto; background: none; color: #000; } }
    ${_fontScaleCss}
  </style>
</head>
<body style="max-width:72.1mm;margin:0 auto;background:none;color:#000;">
  ${logoHtml}
  <div class="center" style="font-weight:bold;font-size:16px;">${nomeEmpresa}</div>
  ${(company.slogan || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;color:#333;margin-bottom:4px;">${company.slogan}</div>` : ""}
  ${(company.address || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;margin-bottom:4px;">${company.address}</div>` : ""}
  ${(company.phone || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;margin-bottom:4px;">TEL: ${fmtTelefone(company.phone)}</div>` : ""}
  ${(company.cnpj || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;">CNPJ: ${fmtCnpj(company.cnpj)}</div>` : ""}
  <div class="line"></div>
  <div class="center" style="font-size:19px;font-weight:bold;">#${codigoStr}</div>
  ${(["balcao", "totem"].includes((p.modulo || "").toLowerCase()) && p.senha_balcao) ? `<div class="center" style="font-size:28px;font-weight:900;margin:4px 0;">SENHA: ${p.senha_balcao}</div>` : ""}
  <div class="center" style="font-size:13px;font-weight:bold;">${formatCupomDate(p.created_at)}, ${formatCupomTime(p.created_at)}</div>
  ${p.scheduled_at ? `
    <div class="center" style="border:3px solid #000;padding:6px 4px;margin:8px 0;background:#000;color:#fff;">
      <div style="font-size:18px;font-weight:900;letter-spacing:1px;">** PEDIDO AGENDADO **</div>
      <div style="font-size:11px;font-weight:bold;margin-top:2px;">ENTREGA PREVISTA</div>
      <div style="font-size:22px;font-weight:900;letter-spacing:0.5px;">${formatCupomAgendamento(p.scheduled_at)}</div>
    </div>
  ` : ""}
  <div class="line"></div>
  <div class="center" style="font-size:28px;font-weight:bold;color:#000;margin:12px 0;">CANCELADO</div>
  <div style="font-weight:bold;margin:6px 0;">Motivo: ${motivo}</div>
  <div class="line"></div>
  <div style="font-weight:bold;">Cliente: ${p.cliente_nome || "—"}</div>
  <div style="display:flex;justify-content:space-between;font-weight:bold;font-size:15px;margin-top:4px;">Total: <span>R$ ${fmtMoeda(Number(p.total) || 0)}</span></div>
  <div class="line"></div>
  <div class="center" style="font-size:11px;font-weight:bold;color:#333;">NÃO É DOCUMENTO FISCAL</div>
</body>
</html>`;
  } else {
  let itensHtml = "";
  // NAO filtra deleted — items cancelados aparecem com marcacao especial pro cliente conferir.
  const items = (p.items || []);
  for (const it of items) {
    const qty = it.quantidade || it.qty || 1;
    const nome = pizzaDisplayName(it);
    const preco = it.preco_unitario || it.preco || it.price || 0;
    // `preco` já é o final_price (inclui adicionais). Linha do item mostra
    // o preço BASE; subitems aparecem em linhas próprias logo abaixo.
    // `sub.preco` JÁ é o total da linha (unitário x quantidade) — convenção
    // do sistema desde sempre (getItemSubitemsHelper grava `preco: preco * qty`).
    // NÃO multiplicar por quantidade aqui: dobraria o valor.
    const subitemsSum = (it.subitems || []).reduce((s, sub) => s + (Number(sub.preco) || 0), 0);
    const precoBase = Math.max(0, preco - subitemsSum);
    const totalItem = precoBase * qty;
    const precoStr = precoBase.toFixed(2).replace(".", ",");
    const comboTag = (it.combo_type || "").toLowerCase() === "combo" ? " ///COMBO" : "";
    const isDeleted = it.deleted === true;

    if (isDeleted) {
      // Item CANCELADO: visual destacado em vermelho com strikethrough
      itensHtml += '<div style="margin-bottom:8px;background:#fee;border:2px solid #c00;padding:4px 6px;border-radius:4px;">';
      itensHtml += `<div style="text-align:center;font-size:11px;font-weight:bold;color:#c00;letter-spacing:1px;margin-bottom:2px;">*** CANCELADO ***</div>`;
      itensHtml += `<div style="display:flex;justify-content:space-between;text-decoration:line-through;color:#666;"><span>${qtdVezesPreco(qty, precoStr, medidaDoItem(it, produtosCadastro))} ${nome}${comboTag}</span><span style="text-decoration:line-through;">R$ ${fmtMoeda(totalItem)}</span></div>`;
      if ((it.observacao || "").trim())
        itensHtml += `<div style="font-size:12px;font-weight:bold;color:#999;margin-left:8px;text-decoration:line-through;">- ${it.observacao}</div>`;
      if (it.subitems && it.subitems.length) {
        for (const s of it.subitems) {
          const _sq = Number(s.quantidade ?? s.qty ?? 1) || 1;
              const nm = (_sq > 1 ? `${_sq}x ` : "") + (s.nome || s.name || "");
          const precoSub = Number(s.preco ?? s.price ?? s.preco_unitario ?? 0) || 0;
          itensHtml += `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#999;margin-left:12px;text-decoration:line-through;"><span>${nm}</span><span>R$ ${fmtMoeda(precoSub)}</span></div>`;
        }
      }
      itensHtml += "</div>";
    } else {
      itensHtml += '<div style="margin-bottom:6px;">';
      itensHtml += `<div style="display:flex;justify-content:space-between;"><span>${qtdVezesPreco(qty, precoStr, medidaDoItem(it, produtosCadastro))} ${nome}${comboTag}</span><span>R$ ${fmtMoeda(totalItem)}</span></div>`;
      if ((it.observacao || "").trim())
        itensHtml += `<div style="font-size:12px;font-weight:bold;color:#333;margin-left:8px;">- ${it.observacao}</div>`;
      if (it.subitems && it.subitems.length) {
        const isPizza = it.product_type === "pizza" || !!it.pizza;
        if (isPizza) {
          const groups = {}; const order = [];
          for (const s of it.subitems) {
            const k = (s.step_name || "Itens").toUpperCase();
            if (!groups[k]) { groups[k] = []; order.push(k); }
            groups[k].push(s);
          }
          for (const key of order) {
            itensHtml += `<div style="font-size:11px;font-weight:bold;color:#666;margin-left:8px;margin-top:2px;">${key}:</div>`;
            for (const s of groups[key]) {
              const _sq = Number(s.quantidade ?? s.qty ?? 1) || 1;
              const nm = (_sq > 1 ? `${_sq}x ` : "") + (s.nome || s.name || "");
              const precoSub = Number(s.preco ?? s.price ?? s.preco_unitario ?? 0) || 0;
              const right = precoSub > 0 ? `+R$ ${fmtMoeda(precoSub)}` : "";
              itensHtml += `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#333;margin-left:16px;"><span>${nm}</span><span>${right}</span></div>`;
            }
          }
        } else {
          for (const s of it.subitems) {
            const _sq = Number(s.quantidade ?? s.qty ?? 1) || 1;
              const nm = (_sq > 1 ? `${_sq}x ` : "") + (s.nome || s.name || "");
            const precoSub = Number(s.preco ?? s.price ?? s.preco_unitario ?? 0) || 0;
            // Mesmo formato do item principal: "{qty} x {unit} {nome}    R${qty*unit}"
            if (precoSub > 0) {
              const subTotal = qty * precoSub;
              const subUnitStr = precoSub.toFixed(2).replace(".", ",");
              itensHtml += `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#333;margin-left:12px;"><span>${qty} x ${subUnitStr} ${nm}</span><span>R$ ${fmtMoeda(subTotal)}</span></div>`;
            } else {
              itensHtml += `<div style="font-size:12px;font-weight:bold;color:#333;margin-left:12px;">${qty} x ${nm}</div>`;
            }
          }
        }
      }
      if ((!it.subitems || !it.subitems.length) && it.etapas && it.etapas.length) {
        for (const ep of it.etapas) {
          const itensEtapa = ep.itens || ep.items || [];
          for (const ei of itensEtapa) {
            const lab = typeof ei === "string" ? ei : (ei.nome || ei.name || "");
            if (!lab) continue;
            const precoEi = typeof ei === "object" && ei != null
              ? (Number(ei.preco ?? ei.price ?? ei.preco_unitario) || 0)
              : null;
            if (precoEi !== null && precoEi > 0) {
              const epTotal = qty * precoEi;
              const epUnitStr = precoEi.toFixed(2).replace(".", ",");
              itensHtml += `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#333;margin-left:12px;"><span>${qty} x ${epUnitStr} ${lab}</span><span>R$ ${fmtMoeda(epTotal)}</span></div>`;
            } else {
              itensHtml += `<div style="font-size:12px;font-weight:bold;color:#333;margin-left:12px;">${qty} x ${lab}</div>`;
            }
          }
        }
      }
      itensHtml += "</div>";
    }
  }
  if (!itensHtml) itensHtml = '<div style="font-size:12px;font-weight:bold;color:#333;">Nenhum item</div>';

  const enderecoCompleto = [p.endereco_rua, p.endereco_numero].filter(Boolean).join(", ");
  const bairro = p.endereco_bairro || "";
  const ref = p.endereco_complemento || "";

  const isPickup = (p.tipo_entrega || "").toLowerCase() === "pickup";
  // Venda feita na loja (Balcão/Totem: comer no local, para viagem, levar
  // agora): sem taxa de entrega, sem endereço e sem "pagar na entrega" — o
  // cupom do Totem pago no cartão saía como se fosse delivery (dono, 25/10/2026)
  const CONSUMO_NA_LOJA = { local: "CONSUMIR NO LOCAL", viagem: "PARA VIAGEM", levar_agora: "LEVAR AGORA" };
  const consumoNaLoja = CONSUMO_NA_LOJA[(p.tipo_entrega || "").toLowerCase()] || null;
  const taxa = Number(p.valor_entrega) || 0;
  const total = Number(p.total) || 0;
  const pagarLabel = consumoNaLoja ? "PAGAMENTO" : isPickup ? "PAGAR NA RETIRADA" : "PAGAR NA ENTREGA";
  const infoLabel = consumoNaLoja ? "PEDIDO NA LOJA" : isPickup ? "INFORMAÇÕES DE RETIRADA" : "INFORMAÇÕES DE ENTREGA";
  const isDinheiro = (p.forma_pagamento || "").toLowerCase().includes("dinheiro");
  const trocoPara = isDinheiro && p.troco_precisa && p.troco_valor != null ? Number(p.troco_valor) : null;
  const trocoLevar = trocoPara != null && trocoPara > total ? trocoPara - total : null;
  const partesPg = partesDoPagamento(p); // dividido: uma linha por forma

  // Verificar se é PIX Online já pago
  const isPixOnlinePago = ((p.forma_pagamento || "").toLowerCase() === "pix_online" || (p.forma_pagamento || "").toLowerCase() === "cartao_online") && p.payment_status === "paid";
  const fsRetLg = 17;
  const fsRetMd = 16;

  const telCliente =
    p.cliente_telefone ?? p.cliente?.telefone ?? p.telefone ?? p.cliente_phone ?? "";

  // Alerta "Contem X" — calculado pelo backend ao criar o pedido com base nas
  // categorias marcadas com print_alert=true (config em Categorias). Aqui só
  // consumimos o label ja gravado no pedido.
  const printAlertLabel = (p.print_alert_label || "").trim();

  // TM-T20X: papel 80mm, área de impressão 72,1mm (58mm em modo 58mm). Margens ~3mm esq, ~5mm dir.
  html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Cupom ${codigoStr}</title>
  <style>
    @page { size: 80mm auto; margin: 3mm; }
    body { margin: 0; padding: 8px; font-family: "Courier New", monospace; font-size: 13px; font-weight: bold; background: none; color: #000000; }
    .line { border-bottom: 1px dashed #000; margin: 6px 0; }
    .center { text-align: center; }
    .section { color: #000; font-weight: bold; margin: 4px 0; }
    @media print { body { max-width: 72.1mm; margin: 0 auto; background: none; color: #000; } }
    ${_fontScaleCss}
  </style>
</head>
<body style="max-width:72.1mm;margin:0 auto;background:none;color:#000;">
  ${logoHtml}
  <div class="center" style="font-weight:bold;font-size:16px;">${nomeEmpresa}</div>
  ${(company.slogan || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;color:#333;margin-bottom:4px;">${company.slogan}</div>` : ""}
  ${(company.address || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;margin-bottom:4px;">${company.address}</div>` : ""}
  ${(company.phone || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;margin-bottom:4px;">TEL: ${fmtTelefone(company.phone)}</div>` : ""}
  ${(company.cnpj || "").trim() ? `<div class="center" style="font-size:12px;font-weight:bold;">CNPJ: ${fmtCnpj(company.cnpj)}</div>` : ""}
  <div class="line"></div>
  ${(p.modulo || "").toLowerCase() === "ifood" ? `<div class="center" style="font-size:22px;font-weight:bold;margin:6px 0;padding:4px;border:2px solid #000;">** iFOOD **</div>` : ""}
  <div class="center" style="font-size:19px;font-weight:bold;">#${codigoStr}</div>
  ${(["balcao", "totem"].includes((p.modulo || "").toLowerCase()) && p.senha_balcao) ? `<div class="center" style="font-size:28px;font-weight:900;margin:4px 0;">SENHA: ${p.senha_balcao}</div>` : ""}
  ${p.ifood_order_id ? `<div class="center" style="font-size:12px;font-weight:bold;color:#333;">iFood: ${p.ifood_display_id || p.ifood_order_id}</div>` : ""}
  <div class="center" style="font-size:13px;font-weight:bold;">${formatCupomDate(p.created_at)}, ${formatCupomTime(p.created_at)}</div>
  ${p.scheduled_at ? `
    <div class="center" style="border:3px solid #000;padding:6px 4px;margin:8px 0;background:#000;color:#fff;">
      <div style="font-size:18px;font-weight:900;letter-spacing:1px;">** PEDIDO AGENDADO **</div>
      <div style="font-size:11px;font-weight:bold;margin-top:2px;">ENTREGA PREVISTA</div>
      <div style="font-size:22px;font-weight:900;letter-spacing:0.5px;">${formatCupomAgendamento(p.scheduled_at)}</div>
    </div>
  ` : ""}
  <div class="line"></div>
  <div class="center section">— ITENS DO PEDIDO —</div>
  <div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#333;margin-bottom:4px;"><span>QTD PREÇO ITEM</span><span>TOTAL</span></div>
  ${itensHtml}
  <div class="line"></div>
  ${(() => {
    const cupomDesc = Number(p.cupom_desconto || 0);
    const descManual = Number(p.desconto_manual || 0);
    const cashbackUsado = Number(p.cashback_usado || 0);
    const hasAnyDesconto = cupomDesc > 0 || descManual > 0 || cashbackUsado > 0;
    // Subtotal real = total + descontos + cashback - taxa (reverso). Soh exibe quando ha desconto pra contextualizar.
    const subtotal = total + cupomDesc + descManual + cashbackUsado - taxa;
    let html = '';
    if (hasAnyDesconto && subtotal > 0) {
      html += `<div style="display:flex;justify-content:space-between;font-weight:bold;">Subtotal:<span>R$ ${fmtMoeda(subtotal)}</span></div>`;
    }
    if (!consumoNaLoja) html += `<div style="display:flex;justify-content:space-between;font-weight:bold;">Taxa de Entrega:<span>(+) R$ ${fmtMoeda(taxa)}</span></div>`;
    if (cupomDesc > 0) {
      html += `<div style="display:flex;justify-content:space-between;font-weight:bold;">Cupom (${p.cupom_codigo || ''}):<span>(-) R$ ${fmtMoeda(cupomDesc)}</span></div>`;
    }
    if (descManual > 0) {
      // Tipo do desconto manual: percent ou fixed (informativo)
      const tipoDesc = (p.desconto_tipo || '').toLowerCase();
      const pctInfo = tipoDesc === 'percent' && p.desconto_percentual ? ` (${Number(p.desconto_percentual).toFixed(1).replace('.', ',')}%)` : '';
      const labelDesc = tipoDesc === 'percent' ? 'Desconto' : 'Desconto manual';
      html += `<div style="display:flex;justify-content:space-between;font-weight:bold;">${labelDesc}${pctInfo}:<span>(-) R$ ${fmtMoeda(descManual)}</span></div>`;
    }
    if (cashbackUsado > 0) {
      html += `<div style="display:flex;justify-content:space-between;font-weight:bold;">Cashback:<span>(-) R$ ${fmtMoeda(cashbackUsado)}</span></div>`;
    }
    return html;
  })()}
  <div style="display:flex;justify-content:space-between;font-weight:bold;font-size:15px;margin-top:4px;">TOTAL:<span>(=) R$ ${fmtMoeda(total)}</span></div>
  <div class="line"></div>
  ${(p.modulo || "").toLowerCase() === "ifood" || (p.forma_pagamento || "").toLowerCase() === "ifood_marketplace" ? `
  <div class="center" style="font-size:28px;font-weight:bold;margin:10px 0;padding:8px;border:3px solid #000;background:#f0f0f0;">PAGO VIA iFOOD</div>
  <div style="display:flex;justify-content:space-between;align-items:center;font-weight:bold;"><span>TOTAL</span><span style="flex:1;border-bottom:1px dotted #000;margin:0 6px;align-self:flex-end;"></span><span>R$ ${fmtMoeda(total)}</span></div>
  <div style="margin-top:4px;font-weight:bold;text-align:center;">Pagamento processado pelo iFood</div>
  ` : isPixOnlinePago ? `
  <div class="center" style="font-size:28px;font-weight:bold;margin:10px 0;padding:8px;border:3px solid #000;background:#f0f0f0;">PEDIDO PAGO</div>
  <div style="display:flex;justify-content:space-between;align-items:center;font-weight:bold;"><span>TOTAL</span><span style="flex:1;border-bottom:1px dotted #000;margin:0 6px;align-self:flex-end;"></span><span>R$ ${fmtMoeda(total)}</span></div>
  <div style="margin-top:4px;font-weight:bold;text-align:center;">Pago via PIX Online</div>
  ` : `
  <div class="center section">— ${pagarLabel} —</div>
  <div style="display:flex;justify-content:space-between;align-items:center;font-weight:bold;"><span>TOTAL</span><span style="flex:1;border-bottom:1px dotted #000;margin:0 6px;align-self:flex-end;"></span><span>R$ ${fmtMoeda(total)}</span></div>
  ${partesPg ? `
  <div style="margin-top:4px;font-weight:bold;">PAGAMENTO DIVIDIDO:</div>
  ${partesPg.map(pg => {
    const label = (pg.label || nomeFormaAgora(pg.method) || "?").toUpperCase();
    let line = `<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:13px;margin-top:2px;"><span>${label}</span><span>R$ ${fmtMoeda(pg.amount || 0)}</span></div>`;
    if (pg.troco_precisa && pg.troco_valor) {
      const trocoVal = pg.troco_valor - (pg.amount || 0);
      line += `<div style="font-size:12px;font-weight:bold;margin-left:8px;">Troco p/ R$ ${fmtMoeda(pg.troco_valor)}${trocoVal > 0 ? ` (levar R$ ${fmtMoeda(trocoVal)})` : ""}</div>`;
    }
    return line;
  }).join("")}
  ` : `
  <div style="margin-top:4px;font-weight:bold;">PAGAMENTO: ${p.forma_pagamento ? nomeFormaAgora(p.forma_pagamento) : "Não definido"}</div>
  ${trocoPara != null ? `<div style="margin-top:2px;font-weight:bold;">Troco para: R$ ${fmtMoeda(trocoPara)}</div>${trocoLevar != null ? `<div style="font-weight:bold;">Troco a levar: R$ ${fmtMoeda(trocoLevar)}</div>` : ""}` : ""}
  `}
  `}
  <div class="line"></div>
  ${printAlertLabel ? `<div class="center section">—ATENÇÃO—</div><div class="center" style="font-size:13px;font-weight:bold;">Contém</div><div class="center" style="font-size:40px;font-weight:bold;">${String(printAlertLabel).toUpperCase()}</div><div class="line"></div>` : ""}
  <div class="center section">— ${infoLabel} —</div>
  ${consumoNaLoja ? `
  ${p.cliente_nome ? `<div style="font-weight:bold;font-size:${fsRetLg}px;">CLIENTE: ${p.cliente_nome}</div>` : ""}
  ${telCliente ? `<div style="font-weight:bold;font-size:${fsRetMd}px;">TEL: ${fmtTelefone(telCliente)}</div>` : ""}
  <div style="font-weight:bold;font-size:${fsRetLg}px;">${consumoNaLoja}</div>
  ` : isPickup ? `
  <div style="font-weight:bold;font-size:${fsRetLg}px;">CLIENTE: ${p.cliente_nome || "—"}</div>
  <div style="font-weight:bold;font-size:${fsRetMd}px;">TEL: ${fmtTelefone(telCliente)}</div>
  <div style="font-weight:bold;font-size:${fsRetLg}px;">RETIRADA NO LOCAL</div>
  ` : `
  <div style="font-weight:bold;font-size:16px;">CLIENTE: ${p.cliente_nome || "—"}</div>
  <div style="font-weight:bold;font-size:15px;">TEL: ${fmtTelefone(telCliente)}</div>
  <div style="font-weight:bold;font-size:16px;">END: ${enderecoCompleto || "—"}</div>
  <div style="font-weight:bold;font-size:15px;">BAIRRO: ${bairro || "—"}</div>
  ${ref ? `<div style="font-weight:bold;font-size:15px;">REF: ${ref}</div>` : ""}
  `}
  <div class="line"></div>
  ${Array.isArray(p.courtesy_coupons) && p.courtesy_coupons.length > 0 ? `
  <div class="center section" style="margin-top:6px;">🎁 CUPONS PARA A PRÓXIMA COMPRA</div>
  <div class="center" style="font-size:11px;margin-bottom:4px;">Válidos apenas para este cliente</div>
  ${p.courtesy_coupons.map(c => {
    const dtype = (c.discount_type || "percentage").toLowerCase();
    const dval = Number(c.discount_value || 0);
    const desconto = dtype === "percentage" ? `${dval.toFixed(0)}% OFF` : `R$ ${fmtMoeda(dval)} OFF`;
    let validStr = "";
    if (c.valid_until) {
      try {
        const d = new Date(c.valid_until);
        validStr = d.toLocaleDateString("pt-BR");
      } catch (_) {}
    }
    const descTxt = (c.description || "").toString().slice(0, 60);
    return `<div style="border:1px dashed #000;padding:6px;margin:6px 0;text-align:center;">
  <div style="font-weight:bold;font-size:14px;letter-spacing:1px;">${c.code}</div>
  <div style="font-weight:bold;font-size:13px;margin-top:2px;">${desconto}</div>
  ${descTxt ? `<div style="font-size:11px;margin-top:2px;">${descTxt}</div>` : ""}
  ${validStr ? `<div style="font-size:10px;margin-top:2px;">Válido até ${validStr}</div>` : ""}
  </div>`;
  }).join("")}
  <div class="center" style="font-size:10px;font-weight:bold;margin-top:2px;">Aplique o código no próximo pedido</div>
  <div class="line"></div>
  ` : ""}
  ${(p.modulo || "").toLowerCase() === "ifood" ? `<div class="center" style="font-size:11px;font-weight:bold;color:#333;margin-bottom:4px;">Pedido recebido via iFood Marketplace</div>` : ""}
  <div class="center" style="font-size:11px;font-weight:bold;color:#333;">NÃO É DOCUMENTO FISCAL</div>
</body>
</html>`;
  }

  // Regras de impressão (impressaoConfig ja carregado acima)
  const printerNames = impressaoConfig.producao || [];

  const empresaData = buildEmpresaPayload(company, logoUrl);
  // Impressão AUTOMÁTICA de pedido com nota fiscal saindo: o cupom de caixa é
  // do servidor (sai com chave e QR Code, ou normal depois de 20 s). A tela
  // imprime só o resto — preparo e fichas continuam aqui.
  const doServidor = opts.automatico && !isCancelado && (await servidorImprimeCaixa(p.id));
  const result = doServidor
    ? { success: true, servidor: true }
    : await smartPrint(html, "CAIXA", printerNames, p, empresaData, p.modulo || null, opts.automatico ? { automatico: true } : null);

  // Após o cupom principal, imprimir tickets individuais se configurado.
  // Passa allProducts quando disponivel pra evitar fetch redundante em /api/products
  // (que em modo totem/sem-auth pode falhar silenciosamente e fazer os tickets nao saírem).
  try {
    await imprimirTickets(p, empresaData, produtosCadastro?.length ? produtosCadastro : opts.allProducts);
  } catch (err) {
    console.warn("[Tickets] Erro ao imprimir tickets:", err);
  }

  return result;
}
